import { html, PropertyValues } from 'lit';
import { property } from 'lit/decorators.js';
import { PublicAppStyles } from './public-app-styles';
import './public-app-content-loader'
import './public-app-header'
import './public-app-footer';
import './public-app-navigation-menu';
import { PageElement } from '../elements/page-element';
import { LoadingCurtain } from '../elements/loading-curtain';
import { PublicAppElement } from './public-app-element';
import { PublicAppNavigationMenu } from './public-app-navigation-menu';
import { UrlResolver } from '../modules/urlResolver';
import { debounce } from '../modules/common';

declare var bootstrap: any;
const MENU_OPEN: string = 'open';
const MENU_CLOSE: string = 'close';
const EVENT_RESIZE: string = 'resize';
const SCREEN_LARGE: number = 992
export class PublicApp extends PublicAppElement {
    static styles = PublicAppStyles.stylesApp;
    static ERROR_PAGE_URL: string = '/loading_error.html';
    private _contentUrl: string = '';        // The URL that is used for populating the widget content
    // Begin getters and setters for properties
    get contentUrl(): string {
        return this._contentUrl;
    }

    @property({ type: String })
    set contentUrl(value: string) {
        const oldValue = this._contentUrl;
        this._contentUrl = value;
        this.requestUpdate('contentUrl', oldValue);
    }

    private _loadingCurtain: LoadingCurtain;
    get loadingCurtain(): LoadingCurtain {
        if (!this._loadingCurtain) this._loadingCurtain = (this.shadowRoot?.getElementById("loading-curtain") || document.getElementById("loading-curtain")) as LoadingCurtain;
        return this._loadingCurtain;
    }

    private _hamburgerMenu: PublicAppNavigationMenu;
    get hamburgerMenu(): PublicAppNavigationMenu {
        if (!this._hamburgerMenu) this._hamburgerMenu = (this.shadowRoot?.getElementById("public-app-navigation-menu") || document.getElementById("public-app-navigation-menu")) as PublicAppNavigationMenu;
        return this._hamburgerMenu;
    }

    private _menuIcon: any;
    get menuIcon(): any {
        if (!this._menuIcon) {
            let header: any = this.getPublicAppElementForSelector("public-app-header")
            this._menuIcon = header && header.getPublicAppElementForSelector("#menu-icon") as any;
        }
        return this._menuIcon;
    }

    private _handleResize = () => {
        if (window.innerWidth >= SCREEN_LARGE) {
            this.toggleHamburgerMenu(MENU_CLOSE)
        }
    }

    firstUpdated(changedProperties: PropertyValues) {
        //-- DETERMINE INITIAL MODULE
        this.loadInitialModule();

        //-- LOAD MENU
        //this.loadMenu();

        //-- LOAD HELP DATA
        //(async () => { await KryptonApp.fetchHelpData(); })();

        //--ADDING RESIZE EVENT HANDLER
        (async () => {
            await new Promise((r) => setTimeout(r, 0));
            window.addEventListener(EVENT_RESIZE, debounce(this._handleResize, 300));
        })();

        //-- ENABLE HISTORY
        this.enableHistory();
        super.firstUpdated(changedProperties);
    }

    async enableHistory() {
        window.onpopstate = e => {
            if (e.state === null) {
                e.preventDefault();
                return false;
            }

            (async () => {
                let url = this.getHomePageUrl(location.hash);
                await this.loadPage(url, false);
            })();

            return true;
        };
    }

    async loadInitialModule() {
        const url = this.getHomePageUrl(location.hash);

        this.loadPage(url);
        //-- TODO put this in a polyfill code for replaceState
        if ("pushState" in history) {
            this.replaceHistoryState({ module: "home" }, document.title, UrlResolver.PUBLIC_RELATIVE.resolve(url));
        } else {
            let scrollV, scrollH;
            //-- Prevent scrolling by storing the page's current scroll offset
            scrollV = document.body.scrollTop;
            scrollH = document.body.scrollLeft;
            location.hash = "";
            //-- Restore the scroll offset, should be flicker free
            document.body.scrollTop = scrollV;
            document.body.scrollLeft = scrollH;
        }
    }
    getHomePageUrl(hash) {
        let url = hash ? hash : this._contentUrl;
        url = this.getCleanUrl(url);
        url = UrlResolver.PUBLIC_RELATIVE.resolve(url);
        return url;
    }

    getCleanUrl(hash) {
        let url = hash ? hash : this._contentUrl;
        let pos = url.lastIndexOf("#nav=");
        url = pos > -1 ? url.substring(pos + 5) : url;
        url = url.replace(/^\/*/, '/');
        let parts = url.split("?");
        if (parts.length > 1) {
            url = parts.shift();
            let params = parts[0].split("&").filter((element, index, array) => !element.startsWith("rand", 0));
            if (params.length > 0)
                url = url + "?" + params.join("&");
        }
        return url;
    }
    getUrlForHistory(url) {
        url = this.getCleanUrl(url);
        let formattedUrl = UrlResolver.PUBLIC_RELATIVE.resolve(url);
        let newUrl = "#nav=" + formattedUrl;// + "#" + formattedUrl;
        return newUrl;
    }

    pushToHistory(page: PageElement) {
        if (page.opener || page.isPageCreatedFromAFormSubmit) {
            return;
        }
        let newUrl = this.getUrlForHistory(page.action);
        history.pushState({ module: page.module, page: page.page }, page.module, newUrl);
    }

    replaceHistoryState(data, title, url) {
        let newUrl = this.getUrlForHistory(url);
        history.replaceState(data, title, newUrl);
    }

    mouseOutHandler(e) {
        let element: any = e.target;
        let buttonImage = element && element.closest("td").querySelector("#showHideIcon")
        let curStyle = element && element.closest("tr").querySelector("#leftNavColumn")?.style?.display;
        if (buttonImage) {
            buttonImage.src = '/images/external/' + (curStyle == "none" ? 'show.gif' : 'hide.gif');
        }
    }

    getElementForSelector(selector: any) {
        let elm = document.querySelector(selector) || this.getPublicAppElementForSelector(selector);
        if (elm) return elm;
        let contLoader: any = this.querySelector("public-app-content-loader") || this.shadowRoot?.querySelector("public-app-content-loader");
        return contLoader && (contLoader?.shadowRoot?.querySelector(selector) || contLoader?.querySelector(selector));
    }
    async loadPage(url, addToHistory: boolean = true) {
        url = this.addRandom(url);
        this.loadingCurtain.open = true;

        let responseText = await this.fetchPage(url);

        const page = new PageElement(url, responseText, undefined, true);
        page["publicAppVersion"] = this.appVersion;
        page.setAttribute("slot", "public_page");
        this.childNodes && this.childNodes.length && this.childNodes.forEach(e => e.remove());
        page.classList.remove("bg-solid-light");
        this.appendChild(page);
        page.applyScriptsAndStyles();
        page.open = true;

        if (addToHistory)
            this.pushToHistory(page)

        this.loadingCurtain.open = false;
        window["initOnPageLoad"] && window["initOnPageLoad"]();
    }

    private async fetchPage(url): Promise<string> {
        const response = await fetch(url)
        let responseText = "";
        if (response.ok) {
            responseText = await response.text()
        } else {
            responseText = await this.getErrorPage()
        }
        return responseText;
    }

    private async getErrorPage(): Promise<string> {
        let responseText = "";
        const response = await fetch(PublicApp.ERROR_PAGE_URL);

        if (response.ok) {
            responseText = await response.text()
        } else {
            responseText = "<div style=\"height:auto; width:auto;background-color:#ffffff;text-align:center;\"><img src=\"/images/warning_icon.gif\" style=\"vertical-align:middle;\"></img><span>Loading failed. Please try again.</span></div>"
        }
        return responseText;
    }

    private addRandom(url: string): string {
        if (url.indexOf("rand") != -1)
            return url;

        var rand = Math.floor(Math.random() * 500);
        if (url.indexOf('?') == -1) {
            url = url + "?rand=" + rand;
        } else {
            url = url + "&rand=" + rand;
        }
        return url;
    }
    createTabbedViewInContainer(containerSelector: string, tabs: any) {
        const cont = this.getElementForSelector(containerSelector);
        const elm = document.createElement("public-app-tab-container")
        setTimeout(() => {
            cont.appendChild(elm);
            elm.tabs = tabs;
        }, 100);
        return elm;
    }

    createKryptonPanel(id: string, details: any) {
        const container = this.getElementForSelector("page-element");
        const elm = document.createElement("public-app-panel");
        elm.setAttribute("id", id);
        container && container.appendChild(elm);
        elm.setAppearance(details);
        if (details.buttons) {
            elm.buttons = details.buttons;
        }
        return elm;
    }

    initBsTooltip(ele) {
        const tooltipTriggerList = [].slice.call(ele?.querySelectorAll('[data-bs-toggle="tooltip"]'))
        tooltipTriggerList?.length > 0 && tooltipTriggerList.map((tooltipTriggerEl: any) => {
            return new bootstrap.Tooltip(tooltipTriggerEl, { trigger: 'hover', customClass: 'grey-tooltip' });
        })
    }

    async toggleNavigationBackdrop(mode?: string) {
        const backdrop = await this.shadowRoot?.querySelector('public-app-navigation-menu');
        if (backdrop) {
            const bachdropEle = backdrop.shadowRoot?.querySelector('#nav-menu-backdrop')
            if (bachdropEle) {
                if (mode === MENU_CLOSE) {
                    bachdropEle.classList.add('d-none')
                    bachdropEle.classList.remove('d-block')
                } else {
                    if (bachdropEle.classList.contains('d-block')) {
                        bachdropEle.classList.add('d-none')
                        bachdropEle.classList.remove('d-block')

                    } else if (bachdropEle.classList.contains('d-none')) {
                        bachdropEle.classList.add('d-block')
                        bachdropEle.classList.remove('d-none')
                    }
                }
            }
        }
    }

    async toggleHamburgerMenu(mode?: string) {
        const sidebar = await this.shadowRoot?.querySelector('.sidebar-nav-menu');
        const menu: any = sidebar?.querySelector('#public-app-navigation-menu')
        const searchInput = menu?.shadowRoot?.querySelector("#nav-menu-search-input")
        const hideSidebar = () => {
            sidebar?.classList.remove('show-sidebar');
            document.body.classList.remove("overflow-hidden");
        }
        const showSidebar = () => {
            sidebar?.classList.add('show-sidebar');
            document.body.classList.add("overflow-hidden")
        }

        if (sidebar) {
            if (mode === MENU_CLOSE) {
                hideSidebar()
            } else {
                if (sidebar.classList.contains('show-sidebar')) {
                    hideSidebar()
                } else {
                    showSidebar()
                }
            }
            //To clear search result if any 
            if (searchInput.value) {
                menu.onSearch('')
            }
        }
        this.toggleNavigationBackdrop(mode ? mode : "")
    }

    collapseDesktopNavigationMenu(mode?: string) {
        // if mode if open nav bar will be made default on click else it will toggle
        const sidebar = this.shadowRoot?.querySelector('#leftNavColumn');
        if (sidebar) {
            const navMenu = sidebar.querySelector('#public-app-navigation-menu')?.shadowRoot
            const sidebarContent = navMenu?.querySelectorAll('.hide-on-collapse-navbar')
            const sidebarContentToShowonCollapse = navMenu?.querySelectorAll('.show-on-collapse-navbar')
            const isNavColumnCollapsed = sidebar.classList.contains('collapse-leftNavColumn')

            const hideElement = (ele: any) => {
                if (!ele.classList.contains('d-none')) {
                    ele.classList.add('d-none')
                }
            }

            const showElement = (ele: any) => {
                if (ele.classList.contains('d-none')) {
                    ele.classList.remove('d-none')
                }
            }

            const expandLeftNavColumn = () => {
                sidebar.classList.remove('collapse-leftNavColumn')
                sidebarContent?.forEach((ele) => {
                    showElement(ele)
                })
                sidebarContentToShowonCollapse?.forEach((ele) => {
                    hideElement(ele)
                })
            }

            if (mode === MENU_OPEN || isNavColumnCollapsed) {
                if (isNavColumnCollapsed) {
                    expandLeftNavColumn()
                }
            } else {
                sidebar.classList.add('collapse-leftNavColumn')
                sidebarContent?.forEach((ele) => {
                    hideElement(ele)
                })
                sidebarContentToShowonCollapse?.forEach((ele) => {
                    showElement(ele)
                })
            }
            if (mode !== MENU_OPEN) {
                this.initBsTooltip(navMenu)
            }
        }
    }

    clearNavMenuSearch = async () => {
        const sidebar = this.shadowRoot?.querySelector('#leftNavColumn')
        const menu: any = sidebar?.querySelector('#public-app-navigation-menu')
        const searchInput = menu?.shadowRoot?.querySelector("#nav-menu-search-input")

        if (searchInput.value) {
            await menu.onSearch('')
            this.collapseDesktopNavigationMenu()
        } else {
            this.collapseDesktopNavigationMenu()
        }
    }

    preventMainMenuHide(e) {
        e && e.stopPropagation();
    }

    render() {
        return html`
            <link rel="stylesheet" href="/css/global.css?v=${this.appVersion}">
            <div class="showOnSmallDevice header-place-holder"></div>
            <div id="responsiveDiv1" class="showOnSmallDevice small-search-place-holder" style="display:none;"></div>
            <public-app-header .systemEnv=${this.runMode} .appVersion=${this.appVersion}></public-app-header>
            <div id="side-bar-nav" class="sidebar-nav-menu d-lg-none" @click=${this.preventMainMenuHide}>  
                <public-app-navigation-menu id="public-app-navigation-menu" .systemEnv=${this.runMode} .appVersion=${this.appVersion} ></public-app-navigation-menu>
            </div>
            <div class="d-flex flex-column content-stretch">
                <div class="d-flex body-content flex-grow-1">
                    <div id="leftNavColumn" class="d-none d-lg-block">
                        <public-app-navigation-menu id="public-app-navigation-menu" .systemEnv=${this.runMode} .appVersion=${this.appVersion} ></public-app-navigation-menu>
                    </div>
                    <div class="container-fluid contentRight">
                        <div class="row">
                            <div class="col-12">
                                <div class="public-page-content-loader">
                                    <!--<public-app-content-loader  .systemEnv=${this.runMode} .appVersion=${this.appVersion}  widgetId="0" url="${this.contentUrl}">
                                    </public-app-content-loader>-->
                                    <div id="slotter">
                                        <slot name="public_page"></slot>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <public-app-footer .systemEnv=${this.runMode} .appVersion=${this.appVersion}></public-app-footer>
            </div>
            <loading-curtain id="loading-curtain" global ?opened=${false}></loading-curtain>
        `;
    }
}
declare global {
    interface HTMLElementTagNameMap {
        'public-app': PublicApp;
    }
}
